/**
 * Translate Firebase auth error codes into user-friendly messages.
 *
 * Used by every auth surface (LoginModal, RegisterModal,
 * ForgotPasswordModal, AuthCard, EmailVerificationPage, useAuth hook)
 * so the global error state and any rendered banner never holds a raw
 * SDK string like "Firebase: Error (auth/invalid-credential).".
 *
 * Important: callers MUST NOT pass `err.message` as `fallbackMessage`.
 * `err.message` from Firebase contains the raw SDK string, and for any
 * unmapped code that string would leak directly to the user. Pass a
 * static fallback (e.g. 'Sign in failed') instead - the unmapped raw
 * message is still logged to the console via the caller's console.error
 * for debuggability.
 *
 * Note on `auth/invalid-credential`: Firebase returns the same code
 * for BOTH wrong-password AND user-not-found (email-enumeration
 * protection). The friendly text preserves that ambiguity rather
 * than leaking which side was wrong.
 */
const FIREBASE_AUTH_ERROR_MAP: Record<string, string> = {
  // Sign-in failures (most common)
  'auth/invalid-credential':       'Email or password is incorrect. Please try again.',
  'auth/wrong-password':           'Email or password is incorrect. Please try again.',
  'auth/user-not-found':           'Email or password is incorrect. Please try again.',
  'auth/invalid-email':            'That email address doesn’t look right. Please check the spelling.',
  'auth/user-disabled':            'This account has been disabled. Contact support if you think this is a mistake.',
  'auth/too-many-requests':        'Too many sign-in attempts. Please wait a few minutes and try again.',
  'auth/network-request-failed':   'Network error. Check your internet connection and try again.',
  'auth/internal-error':           'Something went wrong on our side. Please try again in a moment.',
  'auth/operation-not-allowed':    'Email/password sign-in is not enabled for this app. Contact support.',
  'auth/requires-recent-login':    'Please sign in again to continue.',

  // Sign-up specific
  'auth/email-already-in-use':     'An account with this email already exists. Sign in or use another email.',
  'auth/weak-password':            'Please choose a stronger password (at least 6 characters).',

  // Action codes (verify-email link / password-reset link)
  'auth/expired-action-code':      'This link has expired. Please request a new one.',
  'auth/invalid-action-code':      'This link is invalid or has already been used. Please request a new one.',
  'auth/missing-email':            'Please enter your email address.',
  'auth/missing-password':         'Please enter your password.',

  // Token / session
  'auth/id-token-expired':         'Your session has expired. Please sign in again.',
  'auth/user-token-expired':       'Your session has expired. Please sign in again.',
  'auth/invalid-api-key':          'Authentication is misconfigured. Contact support.',

  // Google / OAuth popup
  'auth/popup-blocked':            'Your browser blocked the Google sign-in popup. Allow popups for stockbai.com and try again.',
  'auth/popup-closed-by-user':     'Google sign-in was cancelled.',
  'auth/cancelled-popup-request':  'Google sign-in was cancelled.',
  'auth/account-exists-with-different-credential':
    'An account with this email already exists using a different sign-in method. Try signing in with the original method.',

  // App-specific
  'auth/new-user-must-register':   'New users must sign up first and agree to the Disclaimer.',

  // Misc
  'auth/quota-exceeded':           'Daily quota exceeded. Please try again tomorrow.',
  'auth/timeout':                  'The request timed out. Please try again.',
  'auth/web-storage-unsupported':  'Your browser blocks the storage needed for sign-in. Enable cookies/storage and try again.',
};

export const getFriendlyAuthError = (
  code: string | undefined | null,
  fallbackMessage: string,
): string => {
  if (code && FIREBASE_AUTH_ERROR_MAP[code]) {
    return FIREBASE_AUTH_ERROR_MAP[code];
  }
  return fallbackMessage;
};
